import { isAxiosError, isCancel } from 'axios'
import { ErrorUnauthorized, ErrorForbidden, ErrorNotFound } from '@/types/errors'

const USER_MESSAGE_MAX_LEN = 220

/** True when the request never received an HTTP response (offline, DNS, CORS, server down, etc.). */
export function isAxiosNetworkError(error: unknown): boolean {
  if (!isAxiosError(error)) {
    return false
  }
  const hasResponse = !!error.response
  if (error.code === 'ERR_CANCELED') {
    return false
  }
  if (isCancel(error)) {
    return false
  }
  return !hasResponse
}

/** HTTP status from an Axios error or from {@link classifyApiError} `statusCode` on `Error`. */
export function getErrorHttpStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'statusCode' in error) {
    const s = (error as { statusCode: unknown }).statusCode
    if (typeof s === 'number' && Number.isFinite(s)) {
      return s
    }
  }
  if (isAxiosError(error) && error.response?.status != null) {
    return error.response.status
  }
  return undefined
}

/**
 * Store / API: use full-page recovery for infrastructure and server failures;
 * keep normal 4xx (except handled 401/403/404) as inline toasts so forms and lists stay usable.
 */
export function shouldOfferFullPageRecovery(error: unknown): boolean {
  if (isAxiosNetworkError(error)) {
    return true
  }
  const status = getErrorHttpStatus(error)
  if (status == null) {
    return error instanceof Error
  }
  if (status >= 500) {
    return true
  }
  if (status === 408 || status === 425 || status === 429) {
    return true
  }
  return false
}

/**
 * Global safety net: any real Error / AxiosError worth showing on the error route (skips benign cases).
 */
export function shouldShowErrorPageForUnhandledReason(reason: unknown): boolean {
  if (reason == null || typeof reason !== 'object') {
    return false
  }
  if (isAxiosError(reason)) {
    if (reason.code === 'ERR_CANCELED') {
      return false
    }
    if (isCancel(reason)) {
      return false
    }
    return true
  }
  return reason instanceof Error
}

const UGLY_AXIOS_STATUS_MESSAGE = /^Request failed with status code \d+$/

export function getUserFacingErrorMessage(
  error: unknown,
  fallback = 'Došlo k neočekávané chybě. Zkuste to prosím znovu.',
): string {
  if (isAxiosNetworkError(error)) {
    return 'Nepodařilo se spojit se serverem. Zkontrolujte připojení k internetu a zkuste to znovu.'
  }
  let raw = fallback
  if (error instanceof Error && error.message) {
    raw = error.message
  } else if (isAxiosError(error) && error.message) {
    raw = error.message
  }
  if (UGLY_AXIOS_STATUS_MESSAGE.test(raw.trim())) {
    raw = fallback
  }
  if (raw.length > USER_MESSAGE_MAX_LEN) {
    return `${raw.slice(0, USER_MESSAGE_MAX_LEN - 1)}…`
  }
  return raw
}

export function getErrorPageTitle(error: unknown): string {
  if (isAxiosNetworkError(error)) {
    return 'Problém s připojením'
  }
  const status = getErrorHttpStatus(error)
  if (status != null && status >= 500) {
    return 'Chyba serveru'
  }
  if (status === 429) {
    return 'Příliš mnoho požadavků'
  }
  if (status === 408) {
    return 'Vypršel čas požadavku'
  }
  return 'Něco se pokazilo'
}

export function buildErrorPageQuery(error: unknown): Record<string, string> {
  const status = getErrorHttpStatus(error)
  const query: Record<string, string> = {
    title: getErrorPageTitle(error),
    message: getUserFacingErrorMessage(error),
    reason: 'app',
  }
  if (isAxiosNetworkError(error)) {
    query.reason = 'network'
  } else if (status != null && status >= 500) {
    query.reason = 'server'
  } else if (status === 429) {
    query.reason = 'rate-limit'
  }
  if (status != null) {
    query.details = `HTTP kód ${status}`
  }
  return query
}

/**
 * Helper function to create error from API response
 */
function createErrorFromApiResponse(
  error: unknown,
  defaultMessage = 'An error occurred',
): Error {
  // Check if it's an axios error
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'status' in error.response
  ) {
    const status = (error.response as { status: number }).status
    const responseData = (error.response as { data?: unknown }).data
    let message: string = defaultMessage
    if (
      responseData &&
      typeof responseData === 'object' &&
      'message' in responseData &&
      typeof responseData.message === 'string'
    ) {
      message = responseData.message
    }

    if (status === 401) {
      return new ErrorUnauthorized(message, responseData)
    }
    if (status === 403) {
      return new ErrorForbidden(message, responseData)
    }
    if (status === 404) {
      return new ErrorNotFound(message, responseData)
    }

    // For other status codes, create a detailed Error
    const detailedError = new Error(message)
    ;(detailedError as Error & { statusCode?: number; response?: unknown }).statusCode = status
    ;(detailedError as Error & { statusCode?: number; response?: unknown }).response = responseData
    return detailedError
  }

  // If it's already an Error, return it
  if (error instanceof Error) {
    return error
  }

  // Otherwise, create a generic error
  return new Error(
    error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
      ? error.message
      : defaultMessage,
  )
}

/**
 * Convert API error to appropriate error class
 */
export function classifyApiError(error: unknown, defaultMessage = 'An error occurred'): Error {
  return createErrorFromApiResponse(error, defaultMessage)
}

/**
 * Check if error is one of the known error types
 */
export function isKnownError(error: unknown): error is ErrorUnauthorized | ErrorForbidden | ErrorNotFound {
  return (
    error instanceof ErrorUnauthorized ||
    error instanceof ErrorForbidden ||
    error instanceof ErrorNotFound
  )
}

